import { toast } from 'sonner';

/**
 * Standardized toast notifications for the app
 */
export const notify = {
  success: (message: string, description?: string) =>
    toast.success(message, { description }),

  error: (message: string, description?: string) =>
    toast.error(message, { description }),

  warning: (message: string, description?: string) =>
    toast.warning(message, { description }),

  info: (message: string, description?: string) =>
    toast.info(message, { description }),

  loading: (message: string) => toast.loading(message),

  dismiss: (id?: string | number) => toast.dismiss(id),

  // AI limit reached — shows upgrade CTA
  aiLimitReached: (type: 'text' | 'cover') => {
    // cerimônia: o tinteiro secou
    window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'inkdry' } }));
    return toast.error(
      type === 'cover'
        ? 'Limite de gerações de capa atingido'
        : 'Limite de ações de IA atingido',
      {
        description: 'Faça upgrade do seu plano para continuar.',
        action: {
          label: 'Ver Planos',
          onClick: () => (window.location.href = '/app/account'),
        },
        duration: 8000,
      }
    );
  },

  // Paid add-on required (ia-studio / craft / outlining) — mirrors the server's
  // AddonRequiredError (HTTP 402, error:'addon_required'). Shows an upsell CTA
  // to the in-app add-ons screen instead of a generic failure toast.
  addonRequired: (addonId: string, message?: string) => {
    const ADDON_NAMES: Record<string, string> = {
      'ia-studio': 'IA Studio',
      craft: 'Craft',
      outlining: 'Outlining',
      bundle: 'Bundle "Tudo"',
    };
    const name = ADDON_NAMES[addonId] ?? 'add-on';
    return toast.error(`Recurso do add-on ${name}`, {
      description: message || `Ative o add-on ${name} para usar este recurso.`,
      action: {
        label: 'Ver add-ons',
        onClick: () => (window.location.href = '/app/account'),
      },
      duration: 8000,
    });
  },

  // Subscription-specific toasts
  limitReached: (feature: string, plan: string) =>
    toast.error(`Limite de ${feature} atingido`, {
      description: `Seu plano ${plan} não permite mais. Faça upgrade para continuar.`,
      action: {
        label: 'Ver Planos',
        onClick: () => (window.location.href = '/pricing'),
      },
      duration: 6000,
    }),

  trialEnding: (days: number) =>
    toast.warning(`Trial termina em ${days} dias`, {
      description: 'Faça upgrade para não perder acesso às features.',
      action: {
        label: 'Fazer Upgrade',
        onClick: () => (window.location.href = '/pricing'),
      },
      duration: 8000,
    }),

  upgradeRequired: (feature: string, requiredPlan: string) =>
    toast.error(`${feature} requer plano ${requiredPlan}`, {
      description: 'Faça upgrade para acessar esta funcionalidade.',
      action: {
        label: 'Fazer Upgrade',
        onClick: () => (window.location.href = '/pricing'),
      },
      duration: 6000,
    }),

  // Common actions
  saved: () => toast.success('Salvo com sucesso'),

  deleted: (item: string) => toast.success(`${item} excluído`),

  copied: () => toast.success('Copiado para a área de transferência'),

  networkError: () =>
    toast.error('Erro de conexão', {
      description: 'Verifique sua internet e tente novamente.',
    }),

  // Promise-based toast for async operations
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    }
  ) => toast.promise(promise, messages),
};

export { toast };
